import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post } from '../lib/api.js';

// ─── Local Types ──────────────────────────────────────────────

type Quarter = 'Q1' | 'Q2' | 'Q3' | 'Q4';

interface QbrMetrics {
  new_breach_count: number;
  total_active_breaches: number;
  passwords_exposed: number;
  breaches_resolved: number;
  training_completion_rate: number;
  users_trained: number;
  overdue_training_count: number;
  phishing_campaigns_run: number;
  phishing_click_rate: number;
  phishing_report_rate: number;
  dmarc_policy: string;
  dmarc_pass_rate: number;
  email_alerts_resolved: number;
  blacklist_events: number;
  posture_events_total: number;
  posture_events_critical: number;
  posture_resolution_rate: number;
  shadow_ai_discoveries: number;
  shadow_ai_blocked: number;
  security_culture_score?: number;
  security_culture_grade?: string;
  security_culture_trend?: string;
}

interface RecommendedAction {
  priority: number;
  action: string;
  business_impact: string;
  effort: 'low' | 'medium' | 'high';
}

interface NarrativeReport {
  id: string;
  quarter: Quarter;
  year: number;
  period_start: string;
  period_end: string;
  client_name: string;
  msp_name?: string;
  executive_summary: string;
  human_risk_narrative: string;
  email_security_narrative: string;
  cloud_saas_narrative: string;
  top_threats_narrative: string;
  achievements_narrative: string;
  recommended_actions: RecommendedAction[];
  metrics: QbrMetrics;
  status: 'draft' | 'approved' | 'delivered';
  approved_by?: string;
  approved_at?: string;
  delivered_at?: string;
  recipient_emails?: string[];
  generated_by: 'ai' | 'manual';
  ai_model?: string;
  generation_duration_ms?: number;
  created_at: string;
  updated_at: string;
}

interface ListResponse {
  data: NarrativeReport[];
  hasMore: boolean;
}

interface SingleResponse {
  data: NarrativeReport;
}

const GENERATION_STEPS = [
  'Collecting security metrics for the quarter...',
  'Analyzing dark web exposure data...',
  'Generating executive summary...',
  'Analyzing human risk data...',
  'Reviewing email security posture...',
  'Evaluating cloud & SaaS security...',
  'Identifying top threats...',
  'Crafting recommendations...',
  'Finalizing report...',
];

const VALID_SECTIONS = [
  { key: 'executive_summary', label: 'Executive Summary' },
  { key: 'human_risk_narrative', label: 'Human Risk' },
  { key: 'email_security_narrative', label: 'Email Security' },
  { key: 'cloud_saas_narrative', label: 'Cloud & SaaS' },
  { key: 'top_threats_narrative', label: 'Top Threats' },
  { key: 'achievements_narrative', label: 'Achievements' },
  { key: 'recommended_actions', label: 'Recommended Actions' },
] as const;

type SectionKey = typeof VALID_SECTIONS[number]['key'];

// ─── Helper Components ────────────────────────────────────────

function StatusBadge({ status }: { status: 'draft' | 'approved' | 'delivered' }) {
  const classes =
    status === 'delivered' ? 'bg-emerald-100 text-emerald-700' :
    status === 'approved' ? 'bg-blue-100 text-blue-700' :
    'bg-yellow-100 text-yellow-700';
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${classes}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function EffortBadge({ effort }: { effort: 'low' | 'medium' | 'high' }) {
  const classes =
    effort === 'low' ? 'bg-green-100 text-green-700' :
    effort === 'medium' ? 'bg-yellow-100 text-yellow-700' :
    'bg-red-100 text-red-700';
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${classes}`}>
      {effort.charAt(0).toUpperCase() + effort.slice(1)} effort
    </span>
  );
}

function MetricCard({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
      <div className="text-2xl font-bold text-emerald-600">{value}</div>
      <div className="text-xs text-gray-500 uppercase tracking-wide mt-1">{label}</div>
    </div>
  );
}

function NarrativeSection({
  title,
  content,
  sectionKey,
  onRegenerate,
  regenerating,
}: {
  title: string;
  content: string;
  sectionKey: SectionKey;
  onRegenerate: (key: SectionKey) => void;
  regenerating: SectionKey | null;
}) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-800">{title}</h3>
        <button
          onClick={() => onRegenerate(sectionKey)}
          disabled={regenerating !== null}
          className="text-xs text-emerald-600 hover:text-emerald-800 disabled:opacity-50 flex items-center gap-1"
        >
          {regenerating === sectionKey ? (
            <>
              <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Regenerating...
            </>
          ) : (
            <>&#8635; Regenerate</>
          )}
        </button>
      </div>
      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">{content}</p>
    </div>
  );
}

// ─── Generate Modal ───────────────────────────────────────────

function GenerateModal({
  onClose,
  onSuccess,
}: {
  onClose: () => void;
  onSuccess: () => void;
}) {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const defaultQuarter: Quarter =
    currentMonth < 3 ? 'Q1' :
    currentMonth < 6 ? 'Q2' :
    currentMonth < 9 ? 'Q3' : 'Q4';

  const [quarter, setQuarter] = useState<Quarter>(defaultQuarter);
  const [year, setYear] = useState(currentYear);
  const [clientName, setClientName] = useState('');
  const [mspName, setMspName] = useState('');
  const [generating, setGenerating] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);

  async function handleGenerate(e: React.FormEvent) {
    e.preventDefault();
    if (!clientName.trim()) return;

    setGenerating(true);
    setError(null);
    setStepIndex(0);

    // Animate through steps
    const interval = setInterval(() => {
      setStepIndex(prev => {
        if (prev < GENERATION_STEPS.length - 1) return prev + 1;
        clearInterval(interval);
        return prev;
      });
    }, 1800);

    try {
      await post<SingleResponse>('/protect/threat-narrative?action=generate', {
        quarter,
        year,
        client_name: clientName.trim(),
        msp_name: mspName.trim() || undefined,
      });
      clearInterval(interval);
      onSuccess();
      onClose();
    } catch (err) {
      clearInterval(interval);
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">Generate QBR Report</h2>
          <button
            onClick={onClose}
            disabled={generating}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            &#x2715;
          </button>
        </div>

        {generating ? (
          <div className="px-6 py-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-50 rounded-full mb-4">
              <svg className="animate-spin h-8 w-8 text-emerald-600" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-900 mb-2">Generating AI Report</p>
            <p className="text-sm text-emerald-600 min-h-[20px]">{GENERATION_STEPS[stepIndex]}</p>
            <div className="mt-4 w-full bg-gray-100 rounded-full h-1.5">
              <div
                className="bg-emerald-500 h-1.5 rounded-full transition-all duration-500"
                style={{ width: `${Math.round(((stepIndex + 1) / GENERATION_STEPS.length) * 100)}%` }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-3">This may take 10–20 seconds</p>
          </div>
        ) : (
          <form onSubmit={handleGenerate} className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quarter</label>
                <select
                  value={quarter}
                  onChange={e => setQuarter(e.target.value as Quarter)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="Q1">Q1 (Jan–Mar)</option>
                  <option value="Q2">Q2 (Apr–Jun)</option>
                  <option value="Q3">Q3 (Jul–Sep)</option>
                  <option value="Q4">Q4 (Oct–Dec)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                <select
                  value={year}
                  onChange={e => setYear(parseInt(e.target.value, 10))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={clientName}
                onChange={e => setClientName(e.target.value)}
                placeholder="Acme Corporation"
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                MSP Name <span className="text-gray-400 text-xs">(optional — appears on cover page)</span>
              </label>
              <input
                type="text"
                value={mspName}
                onChange={e => setMspName(e.target.value)}
                placeholder="Your MSP Name"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                className="flex-1 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 transition-colors"
              >
                Generate with AI
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Report Viewer ────────────────────────────────────────────

function ReportViewer({
  report,
  onBack,
  onRefresh,
}: {
  report: NarrativeReport;
  onBack: () => void;
  onRefresh: () => void;
}) {
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState<SectionKey | null>(null);
  const [deliverEmail, setDeliverEmail] = useState('');
  const [showDeliverForm, setShowDeliverForm] = useState(false);
  const [current, setCurrent] = useState<NarrativeReport>(report);

  async function handleApprove() {
    setActionLoading('approve');
    try {
      const res = await post<SingleResponse>(`/protect/threat-narrative/${current.id}?action=approve`);
      setCurrent(res.data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve report');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDeliver(e: React.FormEvent) {
    e.preventDefault();
    setActionLoading('deliver');
    try {
      const emails = deliverEmail.trim()
        ? deliverEmail.split(',').map(s => s.trim()).filter(Boolean)
        : undefined;
      const res = await post<SingleResponse>(`/protect/threat-narrative/${current.id}?action=deliver`, {
        recipient_emails: emails,
      });
      setCurrent(res.data);
      setShowDeliverForm(false);
      setDeliverEmail('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to mark as delivered');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleRegenerate(sectionKey: SectionKey) {
    setRegenerating(sectionKey);
    try {
      const res = await post<SingleResponse>(`/protect/threat-narrative/${current.id}?action=regenerate_section`, {
        section: sectionKey,
      });
      setCurrent(res.data);
      onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to regenerate section');
    } finally {
      setRegenerating(null);
    }
  }

  const m = current.metrics;

  return (
    <div>
      {/* Back + Toolbar */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
        >
          &#8592; Back to Reports
        </button>
        <div className="flex items-center gap-2">
          <StatusBadge status={current.status} />
          <span className="text-xs text-gray-400">
            {current.quarter} {current.year} &bull; {current.client_name}
          </span>
        </div>
      </div>

      {/* Action Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        {current.status === 'draft' && (
          <button
            onClick={handleApprove}
            disabled={actionLoading !== null}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {actionLoading === 'approve' ? 'Approving...' : 'Approve'}
          </button>
        )}
        {(current.status === 'draft' || current.status === 'approved') && (
          <button
            onClick={() => setShowDeliverForm(v => !v)}
            disabled={actionLoading !== null}
            className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
          >
            Deliver
          </button>
        )}
        <a
          href={`/api/protect/threat-narrative/${current.id}?action=html`}
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
        >
          View Full HTML
        </a>
        <a
          href={`/api/protect/threat-narrative/${current.id}?action=html`}
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50"
          download={`${current.client_name}-Security-Report-${current.quarter}-${current.year}.html`}
        >
          Download
        </a>
        <div className="ml-auto text-xs text-gray-400">
          Generated by {current.generated_by === 'ai' ? `AI (${current.ai_model ?? 'gpt-4o'})` : 'template'}
          {current.generation_duration_ms && ` in ${(current.generation_duration_ms / 1000).toFixed(1)}s`}
        </div>
      </div>

      {/* Deliver Form */}
      {showDeliverForm && (
        <form onSubmit={handleDeliver} className="flex items-center gap-3 mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <input
            type="text"
            value={deliverEmail}
            onChange={e => setDeliverEmail(e.target.value)}
            placeholder="recipient@example.com, another@example.com (optional)"
            className="flex-1 border border-blue-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={actionLoading === 'deliver'}
            className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
          >
            {actionLoading === 'deliver' ? 'Marking...' : 'Mark Delivered'}
          </button>
          <button
            type="button"
            onClick={() => setShowDeliverForm(false)}
            className="px-3 py-2 text-gray-600 text-sm hover:text-gray-800"
          >
            Cancel
          </button>
        </form>
      )}

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6">
        <MetricCard value={`${Math.round(m.training_completion_rate * 100)}%`} label="Training" />
        <MetricCard value={`${Math.round((1 - m.phishing_click_rate) * 100)}%`} label="Phishing Resilience" />
        <MetricCard value={`${Math.round(m.dmarc_pass_rate * 100)}%`} label="DMARC Pass" />
        <MetricCard value={m.total_active_breaches} label="Active Exposures" />
        <MetricCard value={m.posture_events_critical} label="Critical Cloud" />
        <MetricCard value={m.security_culture_grade ?? 'N/A'} label="Culture Grade" />
      </div>

      {/* Narrative Sections */}
      <div className="space-y-4 mb-6">
        <NarrativeSection
          title="Executive Summary"
          content={current.executive_summary}
          sectionKey="executive_summary"
          onRegenerate={handleRegenerate}
          regenerating={regenerating}
        />
        <NarrativeSection
          title="Human Risk & Security Awareness"
          content={current.human_risk_narrative}
          sectionKey="human_risk_narrative"
          onRegenerate={handleRegenerate}
          regenerating={regenerating}
        />
        <NarrativeSection
          title="Email Security"
          content={current.email_security_narrative}
          sectionKey="email_security_narrative"
          onRegenerate={handleRegenerate}
          regenerating={regenerating}
        />
        <NarrativeSection
          title="Cloud & SaaS Security"
          content={current.cloud_saas_narrative}
          sectionKey="cloud_saas_narrative"
          onRegenerate={handleRegenerate}
          regenerating={regenerating}
        />
        <NarrativeSection
          title="Top Threats This Quarter"
          content={current.top_threats_narrative}
          sectionKey="top_threats_narrative"
          onRegenerate={handleRegenerate}
          regenerating={regenerating}
        />
        <NarrativeSection
          title="Security Achievements & Improvements"
          content={current.achievements_narrative}
          sectionKey="achievements_narrative"
          onRegenerate={handleRegenerate}
          regenerating={regenerating}
        />
      </div>

      {/* Recommended Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-800">Recommended Actions</h3>
          <button
            onClick={() => handleRegenerate('recommended_actions')}
            disabled={regenerating !== null}
            className="text-xs text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
          >
            {regenerating === 'recommended_actions' ? 'Regenerating...' : '↻ Regenerate'}
          </button>
        </div>
        <div className="space-y-3">
          {current.recommended_actions.map((action, i) => (
            <div
              key={action.priority}
              className={`p-3 rounded-lg border-l-4 ${
                i === 0 ? 'border-red-500 bg-red-50' :
                i === 1 ? 'border-orange-500 bg-orange-50' :
                i === 2 ? 'border-yellow-500 bg-yellow-50' :
                'border-gray-300 bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">{i + 1}. {action.action}</p>
                  <p className="text-xs text-gray-600 mt-1">{action.business_impact}</p>
                </div>
                <EffortBadge effort={action.effort} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Appendix: Metrics Table */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-base font-semibold text-gray-800 mb-4">Appendix: Full Metrics</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-emerald-600 text-white">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-medium">Category</th>
                <th className="text-left px-3 py-2 text-xs font-medium">Metric</th>
                <th className="text-left px-3 py-2 text-xs font-medium">Value</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Dark Web', 'New Breaches This Quarter', m.new_breach_count],
                ['Dark Web', 'Active Exposures', m.total_active_breaches],
                ['Dark Web', 'Passwords Exposed', m.passwords_exposed],
                ['Training', 'Completion Rate', `${Math.round(m.training_completion_rate * 100)}%`],
                ['Training', 'Users Trained', m.users_trained],
                ['Training', 'Overdue Assignments', m.overdue_training_count],
                ['Phishing', 'Campaigns Run', m.phishing_campaigns_run],
                ['Phishing', 'Click Rate', `${Math.round(m.phishing_click_rate * 100)}%`],
                ['Phishing', 'Report Rate', `${Math.round(m.phishing_report_rate * 100)}%`],
                ['Email', 'DMARC Policy', m.dmarc_policy],
                ['Email', 'DMARC Pass Rate', `${Math.round(m.dmarc_pass_rate * 100)}%`],
                ['Email', 'Blacklist Events', m.blacklist_events],
                ['Cloud/SaaS', 'Total Posture Events', m.posture_events_total],
                ['Cloud/SaaS', 'Critical Events', m.posture_events_critical],
                ['Cloud/SaaS', 'Resolution Rate', `${Math.round(m.posture_resolution_rate * 100)}%`],
                ['Cloud/SaaS', 'Shadow AI Discoveries', m.shadow_ai_discoveries],
                ['Cloud/SaaS', 'Shadow AI Blocked', m.shadow_ai_blocked],
              ].map(([cat, metric, val], idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-3 py-2 text-gray-500">{cat}</td>
                  <td className="px-3 py-2 text-gray-700">{metric}</td>
                  <td className="px-3 py-2 font-medium text-gray-900">{val}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function ThreatNarrativePage() {
  const { data: listData, loading, error, refresh } = useApi<ListResponse>('/protect/threat-narrative');
  const [showGenerate, setShowGenerate] = useState(false);
  const [selectedReport, setSelectedReport] = useState<NarrativeReport | null>(null);

  const reports = listData?.data ?? [];

  if (selectedReport) {
    return (
      <div>
        <ReportViewer
          report={selectedReport}
          onBack={() => setSelectedReport(null)}
          onRefresh={refresh}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">QBR Threat Narrative</h2>
          <p className="text-gray-500 text-sm mt-1">
            AI-generated executive security reports for quarterly business reviews.
          </p>
        </div>
        <button
          onClick={() => setShowGenerate(true)}
          className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 transition-colors"
        >
          + Generate Report
        </button>
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="flex items-center justify-center h-48">
          <div className="text-gray-400 text-sm">Loading reports...</div>
        </div>
      )}

      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">{error}</p>
          <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
        </div>
      )}

      {/* Reports Grid */}
      {!loading && !error && (
        <>
          {reports.length === 0 ? (
            <div className="text-center py-16 bg-white border border-dashed border-gray-300 rounded-xl">
              <div className="text-4xl mb-4">&#128196;</div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No reports yet</h3>
              <p className="text-gray-500 text-sm mb-6 max-w-sm mx-auto">
                Generate your first AI-powered QBR security narrative. Written for executives, white-labeled for your clients.
              </p>
              <button
                onClick={() => setShowGenerate(true)}
                className="px-6 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 transition-colors"
              >
                Generate First Report
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reports.map(r => (
                <div
                  key={r.id}
                  className="bg-white border border-gray-200 rounded-xl p-5 hover:border-emerald-300 hover:shadow-sm transition-all cursor-pointer"
                  onClick={() => setSelectedReport(r)}
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg font-bold text-emerald-600">{r.quarter} {r.year}</span>
                        <StatusBadge status={r.status} />
                      </div>
                      <p className="text-base font-semibold text-gray-800">{r.client_name}</p>
                      {r.msp_name && (
                        <p className="text-xs text-gray-500">by {r.msp_name}</p>
                      )}
                    </div>
                    <div className="text-right text-xs text-gray-400">
                      <div>{new Date(r.created_at).toLocaleDateString()}</div>
                      <div className="mt-1">
                        {r.generated_by === 'ai' ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            &#x2728; AI
                          </span>
                        ) : (
                          <span className="text-gray-400">Template</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Quick Metrics */}
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="text-center bg-gray-50 rounded-md py-2">
                      <div className="text-sm font-bold text-emerald-600">
                        {Math.round(r.metrics.training_completion_rate * 100)}%
                      </div>
                      <div className="text-xs text-gray-500">Training</div>
                    </div>
                    <div className="text-center bg-gray-50 rounded-md py-2">
                      <div className={`text-sm font-bold ${r.metrics.total_active_breaches > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {r.metrics.total_active_breaches}
                      </div>
                      <div className="text-xs text-gray-500">Exposures</div>
                    </div>
                    <div className="text-center bg-gray-50 rounded-md py-2">
                      <div className="text-sm font-bold text-gray-800">
                        {r.metrics.security_culture_grade ?? '—'}
                      </div>
                      <div className="text-xs text-gray-500">Culture</div>
                    </div>
                  </div>

                  {/* Executive Summary Preview */}
                  <p className="text-xs text-gray-500 leading-relaxed line-clamp-2">
                    {r.executive_summary.slice(0, 140)}{r.executive_summary.length > 140 ? '...' : ''}
                  </p>

                  {/* Footer */}
                  <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      {r.period_start.slice(0, 10)} – {r.period_end.slice(0, 10)}
                    </span>
                    <span className="text-xs text-emerald-600 font-medium">View Report &#8594;</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Generate Modal */}
      {showGenerate && (
        <GenerateModal
          onClose={() => setShowGenerate(false)}
          onSuccess={refresh}
        />
      )}
    </div>
  );
}
